/**
 * @file staff-profile.ts
 * @description 員工檔案及其子資源的 Zod schema。
 *
 * 覆蓋以下子資源：
 *   - 員工基本資料（updateStaffProfileSchema）
 *   - 聘用合約（createEmploymentSchema / updateEmploymentSchema）
 *   - 職稱與聘用職務（createQualificationSchema / updateQualificationSchema）
 *   - 驗證業務核准類別（createBusinessCategorySchema / updateBusinessCategorySchema）
 *   - 電子檔案綁定（linkStaffDocumentSchema）
 *   - 簽名檔綁定/解除（setSignatureSchema）
 */
import { z } from 'zod';
import { GENDERS, EMPLOYMENT_STATUSES, VERIFICATION_SCHEMES } from '../enums';
// ─── 學歷條目 ─────────────────────────────────────────────────────────────────
/** 學歷條目（學校 + 科系 + 學位） */
export const educationEntrySchema = z.object({
    school: z.string().min(1).max(100),
    major: z.string().max(100).default(''),
    degree: z.string().max(50).optional(),
});
// ─── 員工基本資料 ─────────────────────────────────────────────────────────────
/**
 * 更新員工檔案標量欄位（全部可選/可空）。
 * 前端/API 提交員工個人資料時使用。
 */
export const updateStaffProfileSchema = z.object({
    /** 員工編號（內部識別碼） */
    employeeNo: z.string().max(50).nullish(),
    /** 性別 */
    gender: z.enum(GENDERS).nullish(),
    /** 出生日期，ISO date 格式 'YYYY-MM-DD' */
    birthDate: z.string().nullish(),
    /** 證件類型（身份證、護照等） */
    idType: z.string().max(30).nullish(),
    /** 證件號碼 */
    idNumber: z.string().max(50).nullish(),
    /** 聯絡地址 */
    address: z.string().max(255).nullish(),
    /** 學歷條目陣列 */
    education: z.array(educationEntrySchema).nullish(),
    /** 工作經歷描述（自由文字） */
    workExperience: z.string().nullish(),
    /** 訓練記錄描述（自由文字） */
    trainingRecord: z.string().nullish(),
    /** 合約來源（外部顧問/內部員工等） */
    contractSource: z.string().max(50).nullish(),
    /** 所屬部門/組別清單 */
    departments: z.array(z.string().max(50)).nullish(),
    /** 備注 */
    notes: z.string().nullish(),
});
// ─── 聘用合約 ─────────────────────────────────────────────────────────────────
/**
 * 新建聘用合約 schema。
 * hiredAt/contractStartAt/contractEndAt 為 ISO date 字串，resignedAt 可空。
 */
export const createEmploymentSchema = z.object({
    /** 到職日期（ISO date） */
    hiredAt: z.string(),
    /** 離職日期（ISO date），可空 */
    resignedAt: z.string().nullish(),
    /** 合約起始日期（ISO date） */
    contractStartAt: z.string(),
    /** 合約終止日期（ISO date） */
    contractEndAt: z.string(),
    /** 聘用狀態 */
    status: z.enum(EMPLOYMENT_STATUSES).default('active'),
    /** 備注 */
    notes: z.string().nullish(),
});
/** 更新聘用合約 schema（所有欄位可選） */
export const updateEmploymentSchema = createEmploymentSchema.partial();
// ─── 職稱與聘用職務 ───────────────────────────────────────────────────────────
/**
 * 新建職稱/聘用職務 schema。
 * jobTitle = 職稱（如稽核員），jobFunction = 聘用職務（如技術評審）。
 */
export const createQualificationSchema = z.object({
    /** 職稱 */
    jobTitle: z.string().min(1).max(50),
    /** 聘用職務 */
    jobFunction: z.string().min(1).max(50),
    /** 是否現行有效 */
    isActive: z.boolean().default(true),
});
/** 更新職稱/聘用職務 schema（所有欄位可選） */
export const updateQualificationSchema = createQualificationSchema.partial();
// ─── 驗證業務核准類別 ─────────────────────────────────────────────────────────
/**
 * 新建驗證業務核准類別 schema。
 * scheme 必須屬於 VERIFICATION_SCHEMES（TOP/TAP/ECO）。
 */
export const createBusinessCategorySchema = z.object({
    /** 驗證方案代碼 */
    scheme: z.enum(VERIFICATION_SCHEMES),
    /** 核准業務類別名稱（如有機作物） */
    category: z.string().min(1).max(100),
    /** 核准日期（ISO date），可空 */
    approvedAt: z.string().nullish(),
    /** 到期日期（ISO date），可空 */
    expiresAt: z.string().nullish(),
});
/** 更新驗證業務核准類別 schema（所有欄位可選） */
export const updateBusinessCategorySchema = createBusinessCategorySchema.partial();
// ─── 電子檔案綁定 ─────────────────────────────────────────────────────────────
/**
 * 把已上傳的 file 綁定到員工電子檔案記錄。
 * fileId 對應 files 表主鍵。
 */
export const linkStaffDocumentSchema = z.object({
    /** 已上傳文件的資料庫主鍵 */
    fileId: z.number().int().positive(),
});
// ─── 簽名檔綁定/解除 ──────────────────────────────────────────────────────────
/**
 * 設定或解除員工電子簽名檔。
 * fileId 為 null 表示解除綁定；正整數表示綁定指定文件。
 */
export const setSignatureSchema = z.object({
    /** 簽名文件的資料庫主鍵，null 表示解除綁定 */
    fileId: z.number().int().positive().nullable(),
});
